import React from 'react';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { motion } from 'framer-motion';

interface NewPricingScreenProps {
  onTryFree: () => void;
  onClose: () => void;
  onLegacyPricing: () => void;
  isLegacyUser?: boolean;
  isPostTrialUser?: boolean;
  isPastDueUser?: boolean;
  isBetaUser?: boolean;
}

interface FeatureBoxProps {
  lottieSrc: string;
  title: string;
}

const FeatureBox: React.FC<FeatureBoxProps> = ({ lottieSrc, title }) => (
  <div className="bg-white/10 backdrop-blur-md rounded-xl sm:rounded-2xl p-3 sm:p-4 hover:bg-white/15 transition-all duration-300 group cursor-default flex items-center gap-3 sm:gap-4">
    <div className="w-11 h-11 sm:w-14 sm:h-14 flex items-center justify-center flex-shrink-0 overflow-hidden">
      <DotLottieReact
        src={lottieSrc}
        loop={false}
        autoplay={false}
        className="w-9 h-9 sm:w-12 sm:h-12"
      />
    </div>
    <h3 className="text-white font-bold text-sm sm:text-base leading-tight">{title}</h3>
  </div>
);

const allFeatures = [
  {
    lottieSrc: '/materials/icons/chapter.json',
    title: 'Kompletní učebnice',
    legacyHidden: true
  },
  {
    lottieSrc: '/materials/icons/flashcardlight.json',
    title: 'Flashcards a testy',
    legacyHidden: true
  },
  {
    lottieSrc: '/materials/icons/audio book.lottie',
    title: 'Audioknihy',
    legacyHidden: true
  },
  {
    lottieSrc: '/materials/icons/wired-flat-1865-shooting-stars-hover-pinch.json',
    title: 'AI Tutor 24/7',
    legacyHidden: false
  }
];

const NewPricingScreen: React.FC<NewPricingScreenProps> = ({
  onTryFree,
  onClose,
  onLegacyPricing,
  isLegacyUser = false,
  isPostTrialUser = false,
  isPastDueUser = false,
  isBetaUser = false
}) => {
  const visibleFeatures = isLegacyUser
    ? allFeatures.filter(f => !f.legacyHidden)
    : allFeatures;

  return (
    <div className="w-full max-w-xl px-6 flex flex-col items-center">

      {/* Lottie Rocket Animation */}
      <motion.div
        initial={{ scale: 0.8, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="w-36 h-36 sm:w-48 sm:h-48 mb-3 sm:mb-4"
      >
        <DotLottieReact
          src="/materials/Rocket.lottie"
          loop={true}
          autoplay={true}
          className="w-full h-full"
        />
      </motion.div>

      {/* Heading */}
      <motion.h1
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.5 }}
        className="text-2xl sm:text-3xl md:text-4xl font-bold text-white text-center mb-4 sm:mb-6 lg:mb-8"
      >
        {isPastDueUser ? (
          <>
            Platba <span className="text-red-400">selhala</span>
          </>
        ) : isLegacyUser ? (
          <>
            OSP AI Tutor pro <br />
            <span className="text-brand-orange">ZSV profíky</span>
          </>
        ) : (
          <>
            Studenti s PRO jsou{' '}
            <span className="text-brand-orange">4.2×</span>{' '}
            úspěšnější!
          </>
        )}
      </motion.h1>

      {/* Feature Boxes Grid */}
      <motion.div
        initial="hidden"
        animate="visible"
        variants={{
          hidden: { opacity: 0 },
          visible: {
            opacity: 1,
            transition: {
              delay: 0.3,
              staggerChildren: 0.1
            }
          }
        }}
        className={`grid grid-cols-1 ${isLegacyUser ? '' : 'sm:grid-cols-2'} gap-3 sm:gap-4 w-full mb-6 sm:mb-8`}
      >
        {visibleFeatures.map((feature) => (
          <motion.div
            key={feature.title}
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0 }
            }}
            transition={{ duration: 0.3 }}
          >
            <FeatureBox
              lottieSrc={feature.lottieSrc}
              title={feature.title}
            />
          </motion.div>
        ))}
      </motion.div>

      {/* CTA Button - Duolingo Style */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8, duration: 0.4 }}
        className="w-full"
      >
        <button
          onClick={onTryFree}
          className={`w-full text-base sm:text-lg font-bold py-3.5 sm:py-4 rounded-2xl transition-all border-b-[6px] active:border-b-2 active:translate-y-[4px] shadow-lg shadow-white/10 mb-3 sm:mb-4 ${isPastDueUser
              ? 'bg-red-500 hover:bg-red-600 text-white border-red-700'
              : 'bg-white hover:bg-gray-50 text-gray-900 border-gray-300'
            }`}
        >
          {isPastDueUser ? 'Aktualizovat platební metodu' : isPostTrialUser ? 'Získat Premium' : 'Vyzkoušet za 0,00 CZK'}
        </button>
      </motion.div>

      {/* No Thanks / Close Text */}
      {!isPastDueUser && (
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9, duration: 0.4 }}
          onClick={onClose}
          className="text-white/40 hover:text-white/60 text-xs sm:text-sm font-medium transition-colors mb-6 sm:mb-8"
        >
          Teď ne, díky
        </motion.button>
      )}

      {/* Legacy Pricing Toggle */}
      {isBetaUser && (
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.0, duration: 0.4 }}
          onClick={onLegacyPricing}
          className="text-white/30 hover:text-white/50 text-xs underline underline-offset-2 transition-colors"
        >
          Zobrazit starý design
        </motion.button>
      )}
    </div>
  );
};

export default NewPricingScreen;